import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
  Tooltip
} from "@mui/material";
import Swal from "sweetalert2";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useCallback, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import CustomLoader from "../../components/CustomLoader";
import errorHandler from "../../utils/errorHandler";
import { getSrno } from "../../utils/helpers";
import { toast } from "react-toastify";
import { EditOutlined } from "@mui/icons-material";
import EmailTemplateListService from "../../services/emailTemplateLits";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { debounce } from "lodash";

export default function EmailTemplateList() {
  const navigate = useNavigate();

  const [paginData, setPaginData] = useState({
    totalCount: 0,
    data: [],
  });
  const [loading, setLoading] = useState(false);
  const [searchItem, setSearchItem] = useState("");
  
 
  const [payload, setPayload] = useState({
    pageSize: 10,
    pageNumber: 1,
    searchItem: "",
    status: [],
    sortObject: { createdAt: -1 },
  });

  const fetchEmailTemplates = useCallback(async () => {
    try {
      setLoading(true);
     
      let res = await EmailTemplateListService.getAll(payload);
      
      
      setPaginData({
        data: res?.data?.data?.result || [],
        totalCount: res?.data?.data?.totalRecords || 0,
      });
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  }, [payload]);

  useEffect(() => {
    fetchEmailTemplates();
  }, [fetchEmailTemplates]);

  const deleteData = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "theme.palette.text.secondary",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await EmailTemplateListService.delete({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            fetchEmailTemplates();
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          console.error("Error deleting...", error);
          errorHandler(error);
        }
      }
    });
  };

  const columns = [
    {
      name: "Sr. No.",
      cell: (row, index) =>
        getSrno(payload.pageNumber, payload.pageSize, index),
      grow: 0.6,
    },
    {
      name: "Template Name",
      sortField: "title",
      sortable: true,
      selector: (row) => row?.title,
    },
    {
      name: "Status",
      sortField: "status",
      sortable: true,
      selector: (row) => (row?.status ? "Active" : "InActive"),
    },
    {
      name: "Created date",
      sortField: "createdAt",
      sortable: true,
      selector: (row) => (row?.created_at ? new Date(row.created_at).toLocaleDateString("en-US") : "NA"),
    },
    {
      name: "Action",
      right: true,
      cell: (row) => (
        <>
          <Tooltip title="Edit" placement="left">
            <IconButton
              color="primary"
              onClick={() => navigate(`/email-template/edit/${row.id}`)}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>

          <Tooltip title="View" placement="top" leaveDelay={50}>
            <IconButton
              color="primary"
              onClick={() => navigate(`/email-template/view/${row.id}`)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete" placement="right">
            <IconButton color="primary" onClick={() => deleteData(row?._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const handleSort = (column, sortDirection) => {
    const srt = {
      [column.sortField]: sortDirection === "asc" ? -1 : 1
    };
    setPayload((prev) => ({
      ...prev,
      sortObject: srt
    }));
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchItem(query);
    debouncedSearch(query);
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      setPayload(prev => ({
        ...prev,
        searchItem: query,
        pageNumber: 1, 
      }));
    }, 400),
    []
  );

  const clearSearch = () => {
    setSearchItem("");
    setPayload(prev => ({
      ...prev,
      searchItem: "",
      pageNumber: 1,
    }));
  };

  return (
    <Card>
      <CardHeader
        titleTypographyProps={{ variant: "h4" }}
        title="Email Template Management"
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          p: 2
        }}
      >
        <Input
          value={searchItem}
          placeholder="Search templates..."
          onChange={handleSearchChange}
          endAdornment={
            searchItem && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={clearSearch}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            )
          }
          sx={{ minWidth: 250 }}
        />
        <Button
          size="large"
          type="submit"
          variant="contained"
          sx={{ ml: 3, mr: 3 }}
          onClick={() => navigate("/email-template/add")}
        >
          Add
        </Button>
      </Box>
      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={paginData?.data}
          pagination
          paginationServer
          paginationTotalRows={paginData?.totalCount}
          onChangePage={(page) =>
            setPayload(prev => ({ ...prev, pageNumber: page }))
          }
          paginationPerPage={payload.pageSize}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          onChangeRowsPerPage={(newPageSize, newPage) => {
            setPayload(prev => ({
              ...prev,
              pageSize: newPageSize,
              pageNumber: 1, 
            }));
          }}
          progressPending={loading}
          onSort={handleSort}
          progressComponent={<CustomLoader />}
        />
      </CardContent>
    </Card>
  );
}